import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { TariffResponseModel } from '../models/tariffs-model'
import { TariffResponseDto } from './dto/tariffs.dto'
import { tariffDtoToModel } from '../transformers/tariff-dto-to-model'
import { MOCK_TARIFFS } from 'src/Libs/e-mobility/guided-tour'
import { usePortalQuery } from 'src/Libs/api'

interface UseFetchTariffsHook {
  tariffResponse: TariffResponseModel
  isLoading: boolean
  isError: boolean
  isRefetching: boolean
  isSuccess: boolean
}

/**
 * Function for fetching tariffs data
 * @returns tariffs as array of TariffModel, isLoading as boolean, isSuccess as boolean, isRefetching as boolean and isError as boolean
 */
export const useFetchTariffs = (installationId: number): UseFetchTariffsHook => {
  const authFetch = useAuthFetchWithResult()
  const eMobilityTourRunning = useTypedSelector(state => state.eMobility.tour.isRunning)

  const fetchTariffs = (installationId: number): Promise<TariffResponseDto> => {
    return authFetch<TariffResponseDto>({
      path: `tariffs/installation/${installationId}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'GET',
    })
  }

  const { data, isLoading, isError, isRefetching, isSuccess } = usePortalQuery({
    queryFn: () => (eMobilityTourRunning ? Promise.resolve(MOCK_TARIFFS) : fetchTariffs(installationId)),
    queryKey: ['tariffs', installationId],
    refetchOnWindowFocus: false,
    /**
     * Always refetch tariffs on mount because they can be modified outside e-mobility section(in settings/smart functions).
     *
     * A "proper" approach would be to trigger the invalidation manually from the smart functions page after changes are saved,
     * but for now we will keep the e-mobility section independent and resilient to changes in outside code. Reduces chance of
     * incorrect queryKey invalidated due to runtime type of installation id(number vs string).
     */
    refetchOnMount: 'always',
    initialData: eMobilityTourRunning ? MOCK_TARIFFS : undefined,
    select: tariffResponseDto => {
      if (!tariffResponseDto) return { dynamicTariffActivated: false, tariffs: [] }
      return {
        dynamicTariffActivated: tariffResponseDto.dynamicTariffActivated,
        tariffs: tariffResponseDto.tariffs.map(tariff => tariffDtoToModel(tariff, tariffResponseDto.dynamicTariffActivated)),
      }
    },
  })

  return {
    tariffResponse: {
      dynamicTariffActivated: data?.dynamicTariffActivated || false,
      tariffs: data?.tariffs || [],
    },
    isLoading,
    isError,
    isRefetching,
    isSuccess,
  }
}
